import * as p from "@clack/prompts";
import { spawn, spawnSync } from "node:child_process";
import { createHash, randomUUID } from "node:crypto";
import { constants as fsConstants, mkdirSync, readFileSync } from "node:fs";
import { access, chmod, copyFile, cp, lstat, mkdir, mkdtemp, readdir, readFile, rm, stat, utimes, writeFile } from "node:fs/promises";
import { homedir, tmpdir } from "node:os";
import path from "node:path";
import { clearTimeout, setTimeout } from "node:timers";
import { setTimeout as delay } from "node:timers/promises";
import pc from "picocolors";
import { parseChecksumFile } from "../checksum-manifest.js";
import { applyDataDirOverride, type DataDirOptionLike } from "../config/data-dir.js";
import { resolveRudderHomeDir } from "../config/home.js";
import { applyLocalEnvProfile, resolveActiveLocalEnvProfile } from "../config/local-env.js";
import {
  DEFAULT_DESKTOP_RELEASE_REPO,
  downloadAsset,
  formatFetchError,
  resolveDesktopDownloadOrigins,
  resolveDesktopDownloadSource,
  resolveDesktopReleaseMirrorBaseUrl,
  withDesktopDownloadOrigins,
  type DesktopDownloadOrigin,
  type GithubReleaseAsset,
} from "../desktop-download.js";
import {
  CLI_NPM_PACKAGE_NAME,
  getGlobalInstalledPackageVersion,
  installPersistentCli,
  resolveGlobalInstalledCliEntry,
  resolvePersistentCliInstallSpec,
} from "../install.js";
import { ensureRuntimeInstalled, resolveRuntimePackageSpec, RuntimeInstallError, type RuntimeInstallResult } from "../runtime/install.js";
import { createByteProgress, formatBytes, type ByteProgressReporter } from "../utils/progress.js";
import { resolveCliVersion } from "../version.js";
import {
  createWindowsBrowserAppShortcut,
  detectSmartAppControlState,
  launchDetachedBrowserApp,
  parseDesktopLaunchMode,
  resolveDesktopLaunchMode,
  resolveEdgeExecutable,
} from "./browser-app.js";
import {
  createDesktopApplySignalController,
  createDesktopProgressFactory,
  writeDesktopProgress,
  type DesktopUpdateProgressPhase,
} from "./desktop-update-progress.js";
export { createDesktopApplySignalController } from "./desktop-update-progress.js";
import { buildWindowsZipExtractCommand, powershellQuote } from "./start-windows.js";

export { parseChecksumFile } from "../checksum-manifest.js";
export {
  buildReleaseMirrorAssetDownloadUrl, DEFAULT_DESKTOP_RELEASE_MIRROR_BASE_URL,
  DEFAULT_DESKTOP_RELEASE_REPO, downloadAsset,
  resolveDesktopDownloadOrigins,
  resolveDesktopDownloadSource,
  resolveDesktopReleaseMirrorBaseUrl,
  withDesktopDownloadOrigins,
  type DesktopDownloadOrigin,
  type DesktopDownloadSource
} from "../desktop-download.js";
export type { GithubReleaseAsset } from "../desktop-download.js";
export { buildWindowsZipExtractCommand } from "./start-windows.js";
export const DESKTOP_UPDATE_QUIT_ARG = "--rudder-update-quit";
export const DESKTOP_UPDATE_FORCE_ARG = "--rudder-update-force";

type SupportedPlatform = "macos" | "windows" | "linux";

export interface DesktopAssetTarget {
  platform: SupportedPlatform;
  arch: "x64" | "arm64";
  extension: ".zip" | ".AppImage";
}

export interface DesktopInstallPaths {
  installRoot: string;
  appPath: string;
  executablePath: string;
  metadataPath: string;
}

interface GithubRelease {
  tag_name: string;
  assets: GithubReleaseAsset[];
}

export type DesktopAssetCandidate = {
  asset: GithubReleaseAsset;
  kind: "shell" | "full";
};

export type ChecksummedDesktopAssetCandidate = DesktopAssetCandidate & {
  expectedChecksum: string;
  warnings: string[];
};

interface StartCommandOptions {
  dataDir?: string;
  cli?: boolean;
  desktop?: boolean;
  serverOnly?: boolean;
  runtime?: boolean;
  version?: string;
  targetVersion?: string;
  repo?: string;
  downloadSource?: string;
  outputDir?: string;
  desktopInstallDir?: string;
  desktopMode?: string;
  localEnv?: string;
  open?: boolean;
  waitForActiveRuns?: boolean;
  desktopProgressJson?: boolean;
  desktopWaitForApply?: boolean;
  desktopPrepareOnly?: boolean;
  desktopAssetPath?: string;
  desktopAssetChecksum?: string;
  desktopAssetName?: string;
  desktopAssetKind?: "full" | "shell";
  desktopReleaseDigest?: string;
  desktopRuntimeBestEffort?: boolean;
  dryRun?: boolean;
  versionCheck?: boolean;
}

export interface DesktopInstallMetadata {
  version: 1;
  releaseTag: string;
  assetName: string;
  assetChecksum: string;
  assetKind?: "full" | "shell";
  installedAt: string;
}

export interface DesktopAssetCacheRetentionOptions {
  now?: Date;
  protectedChecksums?: string[];
  maxEntries?: number;
  maxAgeMs?: number;
  maxTotalBytes?: number;
  keepPreviousEntries?: number;
}

export interface DesktopAssetCachePruneEntry {
  cacheDir: string;
  checksum: string;
  sizeBytes: number;
}

export interface DesktopAssetCachePruneResult {
  scanned: number;
  deleted: DesktopAssetCachePruneEntry[];
  protectedChecksums: string[];
  freedBytes: number;
  warnings: string[];
}

type UpdateQuitResponse =
  | { ok: true; status: "quitting"; pid?: number }
  | { ok: true; status: "not_running" }
  | { ok: false; status: "active_runs"; totalRuns: number }
  | { ok: false; status: "failed"; message: string };

export type ProgressReporterFactory = (label: string) => ByteProgressReporter;

const STABLE_SEMVER_RE = /^[0-9]+\.[0-9]+\.[0-9]+$/;
const CANARY_SEMVER_RE = /^[0-9]+\.[0-9]+\.[0-9]+-canary\.[0-9]+$/;
const CLI_REGISTRY_LATEST_URL = "https://registry.npmjs.org/@rudderhq%2fcli/latest";
const LEGACY_UPDATE_QUIT_GRACE_MS = 10_000;
const UPDATE_QUIT_FORCE_DELAY_MS = 1_000;
const DESKTOP_APP_NAME = "Rudder";
const DESKTOP_METADATA_FILE = ".rudder-desktop-install.json";
const DESKTOP_CHECKSUM_ASSET_NAME = "SHASUMS256.txt";
const DESKTOP_ASSET_CACHE_DIR = "desktop-assets";
const GITHUB_ASSET_DOWNLOAD_ACCEPT = "application/octet-stream";
const DEFAULT_DESKTOP_ASSET_CACHE_MAX_ENTRIES = 2;
const DEFAULT_DESKTOP_ASSET_CACHE_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const DEFAULT_DESKTOP_ASSET_CACHE_MAX_BYTES = 768 * 1024 * 1024;
const DEFAULT_DESKTOP_ASSET_CACHE_KEEP_PREVIOUS = 1;
const DESKTOP_INSTALL_LOCK_TIMEOUT_MS = 60 * 60 * 1000;
const DESKTOP_INSTALL_LOCK_POLL_MS = 250;
export const DESKTOP_RUNTIME_PREPARE_TIMEOUT_MS = 90_000;

async function waitForDesktopRuntimeSmokeEvidence(envName: string): Promise<void> {
  if (process.env.RUDDER_DESKTOP_SMOKE_AUTO_UPDATE_PUBLIC !== "1") return;
  const value = Number(process.env[envName]);
  if (!Number.isFinite(value) || value <= 0) return;
  await delay(Math.min(value, 10_000));
}
const DEFAULT_GITHUB_API_BASE_URL = "https://api.github.com";
const DEFAULT_GITHUB_DOWNLOAD_BASE_URL = "https://github.com";

export function resolveCurrentCliVersion(env: NodeJS.ProcessEnv = process.env): string {
  const version = resolveCliVersion(import.meta.url, env);
  return version === "0.0.0" ? "latest" : version;
}

export function resolveCliInstallSpec(version: string, env: NodeJS.ProcessEnv = process.env): string {
  if (version && version !== "latest") return `${CLI_NPM_PACKAGE_NAME}@${version}`;
  return resolvePersistentCliInstallSpec(env);
}

export function isPersistentCliVersionCurrent(version: string, installedVersion: string | null): boolean {
  return Boolean(version && version !== "latest" && installedVersion === version);
}

export function compareStableSemver(a: string, b: string): number {
  const aMatch = a.match(/^([0-9]+)\.([0-9]+)\.([0-9]+)$/);
  const bMatch = b.match(/^([0-9]+)\.([0-9]+)\.([0-9]+)$/);
  if (!aMatch || !bMatch) return 0;

  for (let index = 1; index <= 3; index += 1) {
    const diff = Number(aMatch[index]) - Number(bMatch[index]);
    if (diff !== 0) return diff;
  }

  return 0;
}

async function fetchLatestCliVersion(): Promise<string | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 2_000);

  try {
    const response = await fetch(CLI_REGISTRY_LATEST_URL, {
      signal: controller.signal,
      headers: { "User-Agent": "rudder-cli-version-check" },
    });
    if (!response.ok) return null;
    const parsed = (await response.json()) as { version?: string };
    return parsed.version?.trim() || null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export async function getCliUpdateNotice(currentVersion: string): Promise<string | null> {
  if (!STABLE_SEMVER_RE.test(currentVersion)) return null;
  const latestVersion = await fetchLatestCliVersion();
  if (!latestVersion || !STABLE_SEMVER_RE.test(latestVersion)) return null;
  if (compareStableSemver(latestVersion, currentVersion) <= 0) return null;

  return `Rudder ${latestVersion} is available. Update with ${pc.cyan(`npx ${CLI_NPM_PACKAGE_NAME}@latest start`)}.`;
}

export function resolveDesktopReleaseTag(version: string): string {
  if (!version || version === "latest") return "latest";
  if (STABLE_SEMVER_RE.test(version)) return `v${version}`;
  if (CANARY_SEMVER_RE.test(version)) return `canary/v${version}`;

  throw new Error(
    `Desktop release lookup requires a release version like 0.1.0 or 0.1.0-canary.0. Received ${version}.`,
  );
}

export function isExactRuntimePackageSpec(version: string, packageSpec: string): boolean {
  return version !== "latest" && packageSpec === resolveRuntimePackageSpec(version);
}

export function runtimeSupportsDesktopShellAssets(
  version: string,
  runtime: Pick<RuntimeInstallResult, "packageSpec" | "postgresPayloadBinDir" | "postgresRuntime">,
): boolean {
  return isExactRuntimePackageSpec(version, runtime.packageSpec)
    && Boolean(runtime.postgresPayloadBinDir)
    && runtime.postgresRuntime?.scope === "shared";
}

export function resolveDesktopAssetTarget(
  platform: NodeJS.Platform = process.platform,
  arch: NodeJS.Architecture = process.arch,
): DesktopAssetTarget {
  if (platform === "darwin") {
    if (arch !== "x64" && arch !== "arm64") {
      throw new Error(`Rudder Desktop does not publish portable assets for ${platform}/${arch}.`);
    }
    return { platform: "macos", arch, extension: ".zip" };
  }
  if (platform === "win32") return { platform: "windows", arch: "x64", extension: ".zip" };
  if (platform === "linux") {
    if (arch !== "x64") {
      throw new Error(`Rudder Desktop does not publish portable assets for ${platform}/${arch}.`);
    }
    return { platform: "linux", arch: "x64", extension: ".AppImage" };
  }

  throw new Error(`Rudder Desktop does not publish portable assets for ${platform}.`);
}

export function resolveDefaultDesktopInstallRoot(
  target: DesktopAssetTarget,
  env: NodeJS.ProcessEnv = process.env,
  homeDir: string = homedir(),
): string {
  if (target.platform === "macos") return path.join(homeDir, "Applications");
  if (target.platform === "windows") {
    const localAppData = env.LOCALAPPDATA?.trim() || path.join(homeDir, "AppData", "Local");
    return path.join(localAppData, "Programs", DESKTOP_APP_NAME);
  }
  return path.join(homeDir, ".local", "share", "rudder");
}

export function resolveDesktopInstallPaths(
  target: DesktopAssetTarget,
  installRoot: string,
): DesktopInstallPaths {
  const root = path.resolve(installRoot);
  if (target.platform === "macos") {
    const appPath = path.join(root, `${DESKTOP_APP_NAME}.app`);
    return {
      installRoot: root,
      appPath,
      executablePath: path.join(appPath, "Contents", "MacOS", DESKTOP_APP_NAME),
      metadataPath: path.join(root, DESKTOP_METADATA_FILE),
    };
  }
  if (target.platform === "windows") {
    return {
      installRoot: root,
      appPath: root,
      executablePath: path.join(root, `${DESKTOP_APP_NAME}.exe`),
      metadataPath: path.join(root, DESKTOP_METADATA_FILE),
    };
  }
  const appPath = path.join(root, `${DESKTOP_APP_NAME}.AppImage`);
  return {
    installRoot: root,
    appPath,
    executablePath: appPath,
    metadataPath: path.join(root, DESKTOP_METADATA_FILE),
  };
}

function normalizeAssetName(name: string): string {
  return name.toLowerCase().replaceAll("_", "-").replaceAll(" ", "-");
}

function scoreDesktopAsset(asset: GithubReleaseAsset, target: DesktopAssetTarget): number {
  const normalized = normalizeAssetName(asset.name);
  const expectedExtension = target.extension.toLowerCase();
  if (!normalized.endsWith(expectedExtension.toLowerCase())) return -1;
  if (normalized.includes("blockmap") || normalized.includes("shasum")) return -1;
  if (normalized.includes("shell")) return -1;

  let score = 1;
  if (normalized.includes("rudder")) score += 2;
  if (normalized.includes(target.platform)) score += 4;
  if (normalized.includes("portable")) score += 6;
  if (target.platform === "macos" && (normalized.includes("macos") || normalized.includes("darwin") || normalized.includes("mac-"))) {
    score += 4;
  }
  if (target.platform === "windows" && (normalized.includes("windows") || normalized.includes("win"))) {
    score += 4;
  }
  if (target.arch === "arm64" && normalized.includes("arm64")) score += 4;
  if (target.arch === "x64" && (normalized.includes("x64") || normalized.includes("amd64"))) score += 4;

  if (target.platform === "macos" && target.arch === "x64" && normalized.includes("arm64")) score -= 10;
  if (target.arch === "arm64" && normalized.includes("x64")) score -= 10;

  return score;
}

export function selectDesktopAsset(
  assets: GithubReleaseAsset[],
  target: DesktopAssetTarget,
): GithubReleaseAsset | null {
  const scored = assets
    .map((asset) => ({ asset, score: scoreDesktopAsset(asset, target) }))
    .filter((item) => item.score >= 0)
    .sort((a, b) => b.score - a.score || a.asset.name.localeCompare(b.asset.name));

  if (scored.length === 0) return null;

  const best = scored[0];
  if (!best) return null;

  const equallyGood = scored.filter((item) => item.score === best.score);
  if (equallyGood.length === 1) return best.asset;

  const exactArch = equallyGood.find((item) => normalizeAssetName(item.asset.name).includes(target.arch));
  return exactArch?.asset ?? best.asset;
}

export function selectDesktopShellAsset(
  assets: GithubReleaseAsset[],
  target: DesktopAssetTarget,
): GithubReleaseAsset | null {
  if (!resolveDesktopShellAssetName("", target)) return null;

  const scored = assets
    .map((asset) => {
      const normalized = normalizeAssetName(asset.name);
      if (!normalized.endsWith(".zip")) return { asset, score: -1 };
      if (!normalized.includes("shell")) return { asset, score: -1 };
      if (!normalized.includes("rudder")) return { asset, score: -1 };
      if (!normalized.includes(target.platform)) return { asset, score: -1 };

      let score = 1;
      if (target.arch === "arm64" && normalized.includes("arm64")) score += 4;
      if (target.arch === "x64" && (normalized.includes("x64") || normalized.includes("amd64"))) score += 4;
      if (target.platform === "macos" && target.arch === "x64" && normalized.includes("arm64")) score -= 10;
      if (target.arch === "arm64" && normalized.includes("x64")) score -= 10;
      return { asset, score };
    })
    .filter((item) => item.score >= 0)
    .sort((a, b) => b.score - a.score || a.asset.name.localeCompare(b.asset.name));

  return scored[0]?.asset ?? null;
}

export function resolveDesktopAssetCandidates(options: {
  releaseAssets: GithubReleaseAsset[];
  target: DesktopAssetTarget;
  repo: string;
  tag: string;
  directReleaseVersion: string | null;
  allowShellAssets?: boolean;
  downloadBaseUrl?: string;
}): DesktopAssetCandidate[] {
  const candidates: DesktopAssetCandidate[] = [];
  const deterministicShellName = options.directReleaseVersion
    ? resolveDesktopShellAssetName(options.directReleaseVersion, options.target)
    : null;
  if (options.allowShellAssets !== false) {
    const shellAsset = selectDesktopShellAsset(options.releaseAssets, options.target)
      ?? (
        options.releaseAssets.length === 0 && deterministicShellName
          ? buildGithubReleaseAsset(
            options.repo,
            options.tag,
            deterministicShellName,
            options.downloadBaseUrl,
          )
          : null
      );
    if (shellAsset) candidates.push({ asset: shellAsset, kind: "shell" });
  }

  const fullAsset = selectDesktopAsset(options.releaseAssets, options.target)
    ?? (
      options.directReleaseVersion
        ? buildGithubReleaseAsset(
          options.repo,
          options.tag,
          resolveDesktopAssetName(options.directReleaseVersion, options.target),
          options.downloadBaseUrl,
        )
        : null
    );
  if (fullAsset) candidates.push({ asset: fullAsset, kind: "full" });

  return candidates;
}

export function selectChecksummedDesktopAssetCandidate(
  candidates: DesktopAssetCandidate[],
  checksums: Map<string, string>,
): ChecksummedDesktopAssetCandidate {
  const warnings: string[] = [];

  for (const candidate of candidates) {
    try {
      return {
        ...candidate,
        expectedChecksum: resolveAssetChecksum(checksums, candidate.asset.name),
        warnings,
      };
    } catch (error) {
      if (candidate.kind === "shell") {
        warnings.push(
          `Layered Desktop shell asset is missing from ${DESKTOP_CHECKSUM_ASSET_NAME}; falling back to the full portable asset.`,
        );
        continue;
      }
      throw error;
    }
  }

  throw new Error("No checksummed Rudder Desktop asset candidate is available.");
}

export function selectChecksumAsset(assets: GithubReleaseAsset[]): GithubReleaseAsset | null {
  return assets.find((asset) => asset.name.toLowerCase() === DESKTOP_CHECKSUM_ASSET_NAME.toLowerCase()) ?? null;
}

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

function githubApiHeaders(): HeadersInit {
  return {
    Accept: "application/vnd.github+json",
    "User-Agent": "rudder-cli-installer",
  };
}

const GITHUB_API_TIMEOUT_MS = 15_000;

export function resolveDesktopSmokeReleaseBaseUrls(env: NodeJS.ProcessEnv = process.env): {
  apiBaseUrl: string;
  downloadBaseUrl: string;
} {
  if (env.RUDDER_DESKTOP_SMOKE_AUTO_UPDATE_PUBLIC !== "1") {
    return {
      apiBaseUrl: DEFAULT_GITHUB_API_BASE_URL,
      downloadBaseUrl: DEFAULT_GITHUB_DOWNLOAD_BASE_URL,
    };
  }
  const normalizeBaseUrl = (value: string | undefined, fallback: string): string => {
    const configured = value?.trim();
    if (!configured) return fallback;
    const parsed = new URL(configured);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      throw new Error(`Desktop smoke release base URL must use HTTP or HTTPS: ${configured}`);
    }
    return configured.replace(/\/+$/u, "");
  };
  return {
    apiBaseUrl: normalizeBaseUrl(
      env.RUDDER_DESKTOP_SMOKE_RELEASE_API_BASE_URL,
      DEFAULT_GITHUB_API_BASE_URL,
    ),
    downloadBaseUrl: normalizeBaseUrl(
      env.RUDDER_DESKTOP_SMOKE_RELEASE_DOWNLOAD_BASE_URL,
      DEFAULT_GITHUB_DOWNLOAD_BASE_URL,
    ),
  };
}

async function fetchGithubRelease(
  repo: string,
  tag: string,
  apiBaseUrl: string = DEFAULT_GITHUB_API_BASE_URL,
): Promise<GithubRelease> {
  const endpoint =
    tag === "latest"
      ? `${apiBaseUrl}/repos/${repo}/releases/latest`
      : `${apiBaseUrl}/repos/${repo}/releases/tags/${encodeURIComponent(tag)}`;
  const response = await fetchWithTimeout(endpoint, { headers: githubApiHeaders() }, GITHUB_API_TIMEOUT_MS);
  if (!response.ok) {
    throw new Error(`GitHub Release ${tag} was not found in ${repo} (${response.status}).`);
  }
  return (await response.json()) as GithubRelease;
}

export function resolveDesktopReleaseVersion(tag: string): string | null {
  if (!tag || tag === "latest") return null;

  const name = tag.split("/").pop() ?? tag;
  if (!name.startsWith("v")) return null;

  const version = name.slice(1);
  if (STABLE_SEMVER_RE.test(version) || CANARY_SEMVER_RE.test(version)) return version;

  return null;
}

export function resolveDesktopAssetName(version: string, target: DesktopAssetTarget): string {
  if (target.platform === "macos") return `${DESKTOP_APP_NAME}-${version}-macos-${target.arch}-portable.zip`;
  if (target.platform === "windows") return `${DESKTOP_APP_NAME}-${version}-windows-x64-portable.zip`;
  return `${DESKTOP_APP_NAME}-${version}-linux-x64.AppImage`;
}

export function resolveDesktopShellAssetName(version: string, target: DesktopAssetTarget): string | null {
  if (target.platform === "macos") return `${DESKTOP_APP_NAME}-${version}-macos-${target.arch}-shell.zip`;
  if (target.platform === "windows") return `${DESKTOP_APP_NAME}-${version}-windows-x64-shell.zip`;
  return null;
}

function encodeReleaseTagForDownloadUrl(tag: string): string {
  return tag.split("/").map((segment) => encodeURIComponent(segment)).join("/");
}

export function buildGithubReleaseAssetDownloadUrl(
  repo: string,
  tag: string,
  assetName: string,
  downloadBaseUrl: string = DEFAULT_GITHUB_DOWNLOAD_BASE_URL,
): string {
  const encodedTag = encodeReleaseTagForDownloadUrl(tag);
  return `${downloadBaseUrl}/${repo}/releases/download/${encodedTag}/${encodeURIComponent(assetName)}`;
}

function buildGithubReleaseAsset(
  repo: string,
  tag: string,
  assetName: string,
  downloadBaseUrl?: string,
): GithubReleaseAsset {
  return {
    name: assetName,
    browser_download_url: buildGithubReleaseAssetDownloadUrl(repo, tag, assetName, downloadBaseUrl),
  };
}

function checksumForFile(filePath: string): string {
  const hash = createHash("sha256");
  hash.update(readFileSync(filePath));
  return hash.digest("hex");
}

export function resolveAssetChecksum(checksums: Map<string, string>, assetName: string): string {
  const expected = checksums.get(path.basename(assetName));
  if (!expected) {
    throw new Error(`Desktop release checksums do not include ${path.basename(assetName)}.`);
  }
  return expected;
}

export function assertChecksumMatch(filePath: string, expected: string): string {
  const actual = checksumForFile(filePath);
  if (actual !== expected.toLowerCase()) {
    throw new Error(`Checksum mismatch for ${path.basename(filePath)}.`);
  }
  return actual;
}

export async function downloadChecksums(
  checksumAsset: GithubReleaseAsset | null,
  outputDir: string,
  progressFactory: ProgressReporterFactory = createByteProgress,
): Promise<Map<string, string>> {
  if (!checksumAsset) {
    throw new Error("Desktop release is missing SHASUMS256.txt.");
  }
  const checksumPath = await downloadAsset(checksumAsset, outputDir, progressFactory);
  return parseChecksumFile(readFileSync(checksumPath, "utf8"));
}

function normalizeDesktopAssetChecksum(checksum: string): string {
  const normalized = checksum.trim().toLowerCase();
  if (!/^[a-f0-9]{64}$/.test(normalized)) {
    throw new Error("Desktop asset cache requires a SHA-256 checksum.");
  }
  return normalized;
}

export function resolveDesktopAssetCacheDir(
  assetChecksum: string,
  homeDir: string = resolveRudderHomeDir(),
): string {
  return path.join(homeDir, DESKTOP_ASSET_CACHE_DIR, normalizeDesktopAssetChecksum(assetChecksum));
}

export function resolveDesktopCachedAssetPath(
  assetName: string,
  assetChecksum: string,
  homeDir: string = resolveRudderHomeDir(),
): string {
  return path.join(resolveDesktopAssetCacheDir(assetChecksum, homeDir), path.basename(assetName));
}

interface DesktopAssetCacheEntry {
  cacheDir: string;
  checksum: string;
  lastUsedAtMs: number;
  sizeBytes: number;
}

export async function pruneDesktopAssetCache(
  options: DesktopAssetCacheRetentionOptions & { homeDir?: string } = {},
): Promise<DesktopAssetCachePruneResult> {
  const homeDir = options.homeDir ?? resolveRudderHomeDir();
  const entries = await scanDesktopAssetCacheEntries(homeDir);
  const protectedChecksums = resolveProtectedDesktopAssetChecksums(entries, {
    protectedChecksums: options.protectedChecksums ?? [],
    keepPreviousEntries: options.keepPreviousEntries ?? DEFAULT_DESKTOP_ASSET_CACHE_KEEP_PREVIOUS,
  });
  const protectedSet = new Set(protectedChecksums);
  const deletions = planDesktopAssetCacheDeletions(entries, {
    nowMs: (options.now ?? new Date()).getTime(),
    protectedChecksums: protectedSet,
    maxEntries: options.maxEntries ?? DEFAULT_DESKTOP_ASSET_CACHE_MAX_ENTRIES,
    maxAgeMs: options.maxAgeMs ?? DEFAULT_DESKTOP_ASSET_CACHE_MAX_AGE_MS,
    maxTotalBytes: options.maxTotalBytes ?? DEFAULT_DESKTOP_ASSET_CACHE_MAX_BYTES,
  });
  const deleted: DesktopAssetCachePruneEntry[] = [];
  const warnings: string[] = [];

  for (const entry of deletions) {
    try {
      await rm(entry.cacheDir, { recursive: true, force: true });
      deleted.push({
        cacheDir: entry.cacheDir,
        checksum: entry.checksum,
        sizeBytes: entry.sizeBytes,
      });
    } catch (error) {
      warnings.push(
        `Failed to remove Desktop asset cache ${entry.cacheDir}: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  return {
    scanned: entries.length,
    deleted,
    protectedChecksums,
    freedBytes: deleted.reduce((total, entry) => total + entry.sizeBytes, 0),
    warnings,
  };
}

async function maybePruneDesktopAssetCache(options: {
  homeDir?: string;
  protectedChecksums: string[];
}): Promise<DesktopAssetCachePruneResult | null> {
  const result = await pruneDesktopAssetCache(options);
  return result.deleted.length > 0 || result.warnings.length > 0 ? result : null;
}

async function scanDesktopAssetCacheEntries(homeDir: string): Promise<DesktopAssetCacheEntry[]> {
  const cacheRoot = path.join(homeDir, DESKTOP_ASSET_CACHE_DIR);
  const dirents = await readdir(cacheRoot, { withFileTypes: true }).catch(() => null);
  if (!dirents) return [];

  const entries: DesktopAssetCacheEntry[] = [];
  for (const dirent of dirents) {
    if (!dirent.isDirectory()) continue;
    let checksum: string;
    try {
      checksum = normalizeDesktopAssetChecksum(dirent.name);
    } catch {
      continue;
    }
    const cacheDir = path.join(cacheRoot, dirent.name);
    const stats = await desktopCacheDirectoryStats(cacheDir);
    entries.push({
      cacheDir,
      checksum,
      lastUsedAtMs: stats.lastUsedAtMs,
      sizeBytes: stats.sizeBytes,
    });
  }
  return entries;
}

async function desktopCacheDirectoryStats(targetPath: string): Promise<{ sizeBytes: number; lastUsedAtMs: number }> {
  const fallbackStat = await stat(targetPath).catch(() => null);
  const dirents = await readdir(targetPath, { withFileTypes: true }).catch(() => null);
  if (!dirents) {
    return {
      sizeBytes: 0,
      lastUsedAtMs: Number(fallbackStat?.mtimeMs ?? 0),
    };
  }

  let sizeBytes = 0;
  let lastUsedAtMs = Number(fallbackStat?.mtimeMs ?? 0);
  for (const dirent of dirents) {
    if (dirent.isSymbolicLink()) continue;
    const entryPath = path.join(targetPath, dirent.name);
    const entryStat = await stat(entryPath).catch(() => null);
    if (!entryStat) continue;
    lastUsedAtMs = Math.max(lastUsedAtMs, Number(entryStat.mtimeMs ?? 0));
    if (dirent.isDirectory()) {
      const nested = await desktopCacheDirectoryStats(entryPath);
      sizeBytes += nested.sizeBytes;
      lastUsedAtMs = Math.max(lastUsedAtMs, nested.lastUsedAtMs);
      continue;
    }
    sizeBytes += Number(entryStat.size ?? 0);
  }
  return { sizeBytes, lastUsedAtMs };
}

function resolveProtectedDesktopAssetChecksums(
  entries: DesktopAssetCacheEntry[],
  options: {
    protectedChecksums: string[];
    keepPreviousEntries: number;
  },
): string[] {
  const protectedChecksums = new Set<string>();
  for (const checksum of options.protectedChecksums) {
    try {
      protectedChecksums.add(normalizeDesktopAssetChecksum(checksum));
    } catch {
      continue;
    }
  }

  const previousEntries = [...entries]
    .filter((entry) => !protectedChecksums.has(entry.checksum))
    .sort((a, b) => b.lastUsedAtMs - a.lastUsedAtMs);
  for (const entry of previousEntries.slice(0, Math.max(0, options.keepPreviousEntries))) {
    protectedChecksums.add(entry.checksum);
  }

  return [...protectedChecksums].sort();
}

function planDesktopAssetCacheDeletions(
  entries: DesktopAssetCacheEntry[],
  options: {
    nowMs: number;
    protectedChecksums: Set<string>;
    maxEntries: number;
    maxAgeMs: number;
    maxTotalBytes: number;
  },
): DesktopAssetCacheEntry[] {
  const deletions = new Set<string>();
  const oldestFirst = [...entries].sort((a, b) => a.lastUsedAtMs - b.lastUsedAtMs);
  const canDelete = (entry: DesktopAssetCacheEntry): boolean =>
    !options.protectedChecksums.has(entry.checksum) && !deletions.has(entry.cacheDir);
  const mark = (entry: DesktopAssetCacheEntry): void => {
    if (canDelete(entry)) deletions.add(entry.cacheDir);
  };

  if (options.maxAgeMs >= 0) {
    for (const entry of oldestFirst) {
      if (options.nowMs - entry.lastUsedAtMs > options.maxAgeMs) mark(entry);
    }
  }

  if (options.maxEntries > 0) {
    for (const entry of oldestFirst) {
      if (entries.length - deletions.size <= options.maxEntries) break;
      mark(entry);
    }
  }

  if (options.maxTotalBytes > 0) {
    let remainingBytes = entries.reduce((total, entry) => total + entry.sizeBytes, 0)
      - [...deletions].reduce((total, cacheDir) => total + (entries.find((entry) => entry.cacheDir === cacheDir)?.sizeBytes ?? 0), 0);
    for (const entry of oldestFirst) {
      if (remainingBytes <= options.maxTotalBytes) break;
      if (!canDelete(entry)) continue;
      deletions.add(entry.cacheDir);
      remainingBytes -= entry.sizeBytes;
    }
  }

  return entries.filter((entry) => deletions.has(entry.cacheDir));
}

async function touchDesktopCachedAsset(cachePath: string): Promise<void> {
  try {
    const now = new Date();
    await utimes(cachePath, now, now);
  } catch {
    // Cache recency should not make a verified Desktop asset unusable.
  }
}

export async function downloadDesktopAssetWithCache(
  asset: GithubReleaseAsset,
  expectedChecksum: string,
  options: {
    homeDir?: string;
    outputDir?: string;
    progressFactory?: ProgressReporterFactory;
  } = {},
): Promise<{ path: string; checksum: string; cacheStatus: "hit" | "miss" }> {
  const normalizedChecksum = normalizeDesktopAssetChecksum(expectedChecksum);
  const cachePath = resolveDesktopCachedAssetPath(asset.name, normalizedChecksum, options.homeDir);

  if (await pathExists(cachePath)) {
    try {
      const checksum = assertChecksumMatch(cachePath, normalizedChecksum);
      await touchDesktopCachedAsset(cachePath);
      return { path: cachePath, checksum, cacheStatus: "hit" };
    } catch {
      await rm(cachePath, { force: true });
    }
  }

  const outputDir = options.outputDir ?? await mkdtemp(path.join(tmpdir(), "rudder-desktop-installer."));
  const removeOutputDir = options.outputDir ? false : true;
  try {
    const downloadedPath = await downloadAsset(asset, outputDir, options.progressFactory, normalizedChecksum);
    const checksum = assertChecksumMatch(downloadedPath, normalizedChecksum);
    await mkdir(path.dirname(cachePath), { recursive: true });
    if (path.resolve(downloadedPath) !== path.resolve(cachePath)) {
      await copyFile(downloadedPath, cachePath);
    }
    return { path: cachePath, checksum, cacheStatus: "miss" };
  } finally {
    if (removeOutputDir) await rm(outputDir, { recursive: true, force: true });
  }
}

async function pathExists(targetPath: string): Promise<boolean> {
  try {
    await access(targetPath, fsConstants.F_OK);
    return true;
  } catch {
    return false;
  }
}

type DesktopInstallLockPayload = {
  lockId: string;
  pid: number;
  installRoot: string;
  createdAt: string;
};

export function resolveDesktopInstallLockPath(paths: DesktopInstallPaths): string {
  const installRootHash = createHash("sha256").update(path.resolve(paths.installRoot)).digest("hex").slice(0, 16);
  return path.join(path.dirname(paths.appPath), `.rudder-desktop-install-${installRootHash}.lock`);
}

async function readDesktopInstallLock(lockPath: string): Promise<DesktopInstallLockPayload | null> {
  try {
    const parsed = JSON.parse(await readFile(lockPath, "utf8")) as Record<string, unknown>;
    if (
      typeof parsed.pid !== "number"
      || !Number.isInteger(parsed.pid)
      || parsed.pid <= 0
      || typeof parsed.lockId !== "string"
      || typeof parsed.installRoot !== "string"
      || typeof parsed.createdAt !== "string"
    ) {
      return null;
    }
    return {
      lockId: parsed.lockId,
      pid: parsed.pid,
      installRoot: parsed.installRoot,
      createdAt: parsed.createdAt,
    };
  } catch {
    return null;
  }
}

export async function withDesktopInstallLock<T>(
  paths: DesktopInstallPaths,
  fn: () => Promise<T>,
  options: {
    timeoutMs?: number;
    pollMs?: number;
  } = {},
): Promise<T> {
  const lockPath = resolveDesktopInstallLockPath(paths);
  const lockDir = path.dirname(lockPath);
  const timeoutMs = options.timeoutMs ?? DESKTOP_INSTALL_LOCK_TIMEOUT_MS;
  const pollMs = options.pollMs ?? DESKTOP_INSTALL_LOCK_POLL_MS;
  const startedAt = Date.now();
  const payload: DesktopInstallLockPayload = {
    lockId: randomUUID(),
    pid: process.pid,
    installRoot: path.resolve(paths.installRoot),
    createdAt: new Date().toISOString(),
  };

  await mkdir(lockDir, { recursive: true });

  while (true) {
    try {
      await writeFile(lockPath, `${JSON.stringify(payload, null, 2)}\n`, { encoding: "utf8", flag: "wx" });
      break;
    } catch (error) {
      const err = error as NodeJS.ErrnoException;
      if (err.code !== "EEXIST") throw error;

      const existing = await readDesktopInstallLock(lockPath);
      const stale = !existing || !processExists(existing.pid);
      if (stale) {
        await rm(lockPath, { force: true });
        continue;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        throw new Error(
          `Timed out waiting for Rudder Desktop install lock for ${paths.appPath}. `
          + `Held by pid ${existing.pid} for ${existing.installRoot}.`,
        );
      }
      await delay(pollMs);
    }
  }

  try {
    return await fn();
  } finally {
    const existing = await readDesktopInstallLock(lockPath);
    if (existing?.lockId === payload.lockId) {
      await rm(lockPath, { force: true });
    }
  }
}

function runChecked(command: string, args: string[], options: { cwd?: string; shell?: boolean } = {}): void {
  const result = spawnSync(command, args, {
    encoding: "utf8",
    stdio: ["ignore", "pipe", "pipe"],
    ...options,
  });
  if (result.status === 0) return;

  const output = [result.stdout, result.stderr]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join("\n")
    .trim();
  throw new Error(`${command} ${args.join(" ")} failed${output ? `: ${output}` : ""}`);
}

function formatCommandFailure(command: string, args: string[], stdout: unknown, stderr: unknown): string {
  const output = [stdout, stderr]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join("\n")
    .trim();
  return `${command} ${args.join(" ")} failed${output ? `: ${output}` : ""}`;
}

export function buildWindowsRobocopyMirrorCommand(sourcePath: string, destinationPath: string): { command: string; args: string[] } {
  return {
    command: "robocopy.exe",
    args: [sourcePath, destinationPath, "/MIR", "/R:2", "/W:1", "/NFL", "/NDL", "/NJH", "/NJS", "/NP"],
  };
}

export function isSuccessfulRobocopyExitCode(status: number | null): boolean {
  return typeof status === "number" && status >= 0 && status <= 7;
}

async function extractZip(zipPath: string, outputDir: string, target: DesktopAssetTarget): Promise<void> {
  await rm(outputDir, { recursive: true, force: true });
  await mkdir(outputDir, { recursive: true });

  if (target.platform === "macos") {
    runChecked("ditto", ["-x", "-k", zipPath, outputDir]);
    return;
  }

  if (target.platform === "windows") {
    const command = buildWindowsZipExtractCommand(zipPath, outputDir);
    runChecked(command.command, command.args);
    return;
  }

  throw new Error(`Zip assets are not supported for ${target.platform}.`);
}

async function findPath(
  root: string,
  predicate: (filePath: string, isDirectory: boolean) => boolean,
  maxDepth = 5,
): Promise<string | null> {
  async function visit(dir: string, depth: number): Promise<string | null> {
    const entries = await readdir(dir, { withFileTypes: true });
    for (const entry of entries) {
      const fullPath = path.join(dir, entry.name);
      if (predicate(fullPath, entry.isDirectory())) return fullPath;
      if (entry.isDirectory() && depth < maxDepth) {
        const nested = await visit(fullPath, depth + 1);
        if (nested) return nested;
      }
    }
    return null;
  }

  return await visit(root, 0);
}

async function findMacApp(extractDir: string): Promise<string> {
  const direct = path.join(extractDir, `${DESKTOP_APP_NAME}.app`);
  if (await pathExists(direct)) return direct;
  const found = await findPath(extractDir, (filePath, isDirectory) =>
    isDirectory && path.basename(filePath) === `${DESKTOP_APP_NAME}.app`);
  if (!found) throw new Error(`Portable macOS archive did not contain ${DESKTOP_APP_NAME}.app.`);
  return found;
}

async function findWindowsAppDir(extractDir: string): Promise<string> {
  const direct = path.join(extractDir, `${DESKTOP_APP_NAME}.exe`);
  if (await pathExists(direct)) return extractDir;
  const executable = await findPath(extractDir, (filePath, isDirectory) =>
    !isDirectory && path.basename(filePath).toLowerCase() === `${DESKTOP_APP_NAME.toLowerCase()}.exe`);
  if (!executable) throw new Error(`Portable Windows archive did not contain ${DESKTOP_APP_NAME}.exe.`);
  return path.dirname(executable);
}

async function readInstallMetadata(metadataPath: string): Promise<DesktopInstallMetadata | null> {
  try {
    const parsed = JSON.parse(await readFile(metadataPath, "utf8")) as DesktopInstallMetadata;
    if (parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function isInstalledDesktopCurrent(
  metadata: DesktopInstallMetadata | null,
  releaseTag: string,
  assetName: string,
  assetChecksum: string,
): boolean {
  return Boolean(
    metadata &&
    metadata.releaseTag === releaseTag &&
    metadata.assetName === assetName &&
    metadata.assetChecksum === assetChecksum,
  );
}

function forceQuitDesktopProcess(pid: number, target: DesktopAssetTarget): void {
  if (target.platform === "windows") {
    spawnSync("taskkill.exe", ["/PID", String(pid), "/T", "/F"], { stdio: "ignore" });
    return;
  }

  try {
    process.kill(pid, "SIGKILL");
  } catch {
    // The process may already have exited between the wait timeout and kill.
  }
}

function quotePowerShellString(value: string): string {
  return `'${value.replaceAll("'", "''")}'`;
}

function findDesktopExecutablePids(executablePath: string, target: DesktopAssetTarget): number[] {
  if (target.platform === "windows") {
    const result = spawnSync("powershell.exe", [
      "-NoProfile",
      "-Command",
      `Get-CimInstance Win32_Process | Where-Object { $_.ExecutablePath -eq ${quotePowerShellString(executablePath)} } | Select-Object -ExpandProperty ProcessId`,
    ], {
      encoding: "utf8",
      stdio: ["ignore", "pipe", "ignore"],
    });
    if (result.status !== 0) return [];
    return result.stdout
      .split(/\r?\n/)
      .map((line) => Number.parseInt(line.trim(), 10))
      .filter((pid) => Number.isInteger(pid) && pid > 0 && pid !== process.pid);
  }

  const result = spawnSync("ps", ["-eo", "pid=,args="], {
    encoding: "utf8",
    stdio: ["ignore", "pipe", "ignore"],
  });
  if (result.status !== 0) return [];

  return result.stdout
    .split(/\r?\n/)
    .flatMap((line) => {
      const match = line.match(/^\s*(\d+)\s+(.+)$/);
      if (!match) return [];
      const pid = Number.parseInt(match[1], 10);
      const commandLine = match[2];
      const matchesExecutable = commandLine === executablePath || commandLine.startsWith(`${executablePath} `);
      return matchesExecutable && pid !== process.pid ? [pid] : [];
    });
}

function isRunningInsideDesktopExecutable(): boolean {
  return path.basename(process.execPath).toLowerCase().startsWith(DESKTOP_APP_NAME.toLowerCase());
}

function desktopApplicationEnvironment(): NodeJS.ProcessEnv {
  const env = { ...process.env };
  // The macOS update CLI runs through Electron with this flag enabled. The
  // quit handoff must launch the real Electron app so it can emit
  // `second-instance`, rather than another Node-mode process.
  delete env.ELECTRON_RUN_AS_NODE;
  return env;
}

async function waitForUpdateQuitResponse(responsePath: string, timeoutMs = 8_000): Promise<UpdateQuitResponse | null> {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (await pathExists(responsePath)) {
      return JSON.parse(await readFile(responsePath, "utf8")) as UpdateQuitResponse;
    }
    await delay(200);
  }
  return null;
}

async function requestDesktopQuit(
  executablePath: string,
  target: DesktopAssetTarget,
  options: { forceUpdate?: boolean; responseTimeoutMs?: number } = {},
): Promise<UpdateQuitResponse | null> {
  if (!(await pathExists(executablePath))) return { ok: true, status: "not_running" };
  const responsePath = path.join(tmpdir(), `rudder-update-quit-${process.pid}-${Date.now()}.json`);
  const result = spawnSync(executablePath, [
    `${DESKTOP_UPDATE_QUIT_ARG}=${responsePath}`,
    ...(options.forceUpdate ? [DESKTOP_UPDATE_FORCE_ARG] : []),
  ], {
    env: desktopApplicationEnvironment(),
    stdio: "ignore",
    timeout: 5_000,
  });
  if (result.error && target.platform === "windows") {
    return null;
  }

  try {
    return await waitForUpdateQuitResponse(responsePath, options.responseTimeoutMs);
  } finally {
    await rm(responsePath, { force: true });
  }
}

function processExists(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch (error) {
    const code = typeof error === "object" && error && "code" in error
      ? String((error as { code?: unknown }).code)
      : "";
    return code === "EPERM";
  }
}

function readUpdateQuitPid(response: UpdateQuitResponse | null): number | null {
  if (!response?.ok || response.status !== "quitting") return null;
  return typeof response.pid === "number" && Number.isInteger(response.pid) && response.pid > 0
    ? response.pid
    : null;
}

function isLegacyUnconfirmedUpdateQuit(response: UpdateQuitResponse | null): boolean {
  return Boolean(response?.ok && response.status === "quitting" && !readUpdateQuitPid(response));
}

export async function waitForProcessExit(pid: number, timeoutMs = 20_000, intervalMs = 250): Promise<boolean> {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (!processExists(pid)) return true;
    await delay(intervalMs);
  }
  return !processExists(pid);
}

async function waitForProcessesExit(
  pids: number[],
  waitForExit: (pid: number) => Promise<boolean>,
): Promise<boolean> {
  const uniquePids = [...new Set(pids)];
  if (uniquePids.length === 0) return true;
  const results = await Promise.all(uniquePids.map((pid) => waitForExit(pid)));
  return results.every(Boolean);
}

async function removePathWithRetry(targetPath: string, attempts = 5): Promise<boolean> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      await rm(targetPath, { recursive: true, force: true });
      if (!(await pathExists(targetPath))) return true;
    } catch {
      // Retry below; Windows can keep files locked briefly after process exit.
    }
    await delay(500);
  }
  return false;
}

export async function prepareForDesktopReplace(
  paths: DesktopInstallPaths,
  target: DesktopAssetTarget,
  options: {
    waitForActiveRuns?: boolean;
    waitForForceUpdate?: (timeoutMs: number) => Promise<boolean>;
    onActiveRunsWaiting?: (totalRuns: number) => void;
    activeRunPollIntervalMs?: number;
    forceUpdate?: boolean;
    legacyUpdateQuitGraceMs?: number;
    updateQuitResponseTimeoutMs?: number;
    updateQuitForceDelayMs?: number;
    forceQuitDesktopProcess?: (pid: number, target: DesktopAssetTarget) => void;
    waitForDesktopProcessExit?: (pid: number) => Promise<boolean>;
    findDesktopExecutablePids?: (executablePath: string, target: DesktopAssetTarget) => number[];
  } = {},
): Promise<void> {
  const forceQuitPid = options.forceQuitDesktopProcess ?? forceQuitDesktopProcess;
  const waitForExit = options.waitForDesktopProcessExit ?? waitForProcessExit;
  const findPids = options.findDesktopExecutablePids ?? findDesktopExecutablePids;
  async function forceQuitPidAndConfirm(pid: number): Promise<void> {
    forceQuitPid(pid, target);
    await delay(options.updateQuitForceDelayMs ?? UPDATE_QUIT_FORCE_DELAY_MS);
    if (!(await waitForExit(pid))) {
      throw new Error(`Rudder Desktop process ${pid} did not exit after force-quit fallback. Close Rudder and rerun start.`);
    }
  }
  async function forceQuitPidsAndConfirm(pids: number[]): Promise<void> {
    const uniquePids = [...new Set(pids)];
    for (const pid of uniquePids) {
      forceQuitPid(pid, target);
    }
    await delay(options.updateQuitForceDelayMs ?? UPDATE_QUIT_FORCE_DELAY_MS);
    if (!(await waitForProcessesExit(uniquePids, waitForExit))) {
      throw new Error(`Rudder Desktop process${uniquePids.length === 1 ? "" : "es"} ${uniquePids.join(", ")} did not exit after force-quit fallback. Close Rudder and rerun start.`);
    }
  }
  let quitPid: number | null = null;
  let managedExecutablePids: number[] = [];
  const hasManagedExecutable = await pathExists(paths.executablePath);
  if (hasManagedExecutable) {
    managedExecutablePids = findPids(paths.executablePath, target);
    let forceUpdate = options.forceUpdate === true;
    const requestQuit = () => requestDesktopQuit(paths.executablePath, target, {
      forceUpdate,
      responseTimeoutMs: options.updateQuitResponseTimeoutMs,
    });
    let quitResponse = await requestQuit();
    while (quitResponse && !quitResponse.ok && quitResponse.status === "active_runs" && options.waitForActiveRuns && !forceUpdate) {
      p.log.warn(
        `Rudder Desktop has ${quitResponse.totalRuns} active run${quitResponse.totalRuns === 1 ? "" : "s"}; waiting before replacing Desktop.`,
      );
      options.onActiveRunsWaiting?.(quitResponse.totalRuns);
      const pollIntervalMs = options.activeRunPollIntervalMs ?? 15_000;
      forceUpdate = options.waitForForceUpdate
        ? await options.waitForForceUpdate(pollIntervalMs)
        : (await delay(pollIntervalMs), false);
      quitResponse = await requestQuit();
    }
    if (quitResponse && !quitResponse.ok && quitResponse.status === "active_runs") {
      if (!forceUpdate) {
        throw new Error(
          `Rudder Desktop has ${quitResponse.totalRuns} active run${quitResponse.totalRuns === 1 ? "" : "s"}. Stop active work, then rerun start.`,
        );
      }
      throw new Error(
        `Rudder Desktop still has ${quitResponse.totalRuns} active run${quitResponse.totalRuns === 1 ? "" : "s"} after the force-update request. Stop active work, then rerun start.`,
      );
    }
    if (quitResponse && !quitResponse.ok && quitResponse.status === "failed") {
      throw new Error(quitResponse.message);
    }
    quitPid = readUpdateQuitPid(quitResponse);
    if (quitPid) {
      p.log.info(`Waiting for existing Rudder Desktop process ${quitPid} to exit before replacing it.`);
      if (!(await waitForExit(quitPid))) {
        p.log.warn(`Rudder Desktop process ${quitPid} did not exit in time; attempting force-quit fallback.`);
        await forceQuitPidAndConfirm(quitPid);
      }
    } else if (isLegacyUnconfirmedUpdateQuit(quitResponse)) {
      const graceMs = options.legacyUpdateQuitGraceMs ?? LEGACY_UPDATE_QUIT_GRACE_MS;
      p.log.warn(
        `Existing Rudder Desktop acknowledged update quit without a process id; waiting ${Math.ceil(graceMs / 1_000)}s before replacement.`,
      );
      await delay(graceMs);
      if (managedExecutablePids.length > 0 && !(await waitForProcessesExit(managedExecutablePids, waitForExit))) {
        p.log.warn(
          `Existing Rudder Desktop did not exit after acknowledging update quit; attempting path-scoped force-quit for process${managedExecutablePids.length === 1 ? "" : "es"} ${managedExecutablePids.join(", ")}.`,
        );
        await forceQuitPidsAndConfirm(managedExecutablePids);
      }
    } else if (!quitResponse) {
      if (options.forceUpdate && managedExecutablePids.length > 0) {
        p.log.warn(
          `Existing Rudder Desktop did not respond to the update quit request; attempting path-scoped force-quit for process${managedExecutablePids.length === 1 ? "" : "es"} ${managedExecutablePids.join(", ")}.`,
        );
        await forceQuitPidsAndConfirm(managedExecutablePids);
      } else {
        throw new Error("Existing Rudder Desktop did not respond to the update quit request. Close Rudder and rerun start.");
      }
    } else {
      await delay(options.updateQuitForceDelayMs ?? UPDATE_QUIT_FORCE_DELAY_MS);
    }
  }

  const replacePath = target.platform === "windows" ? paths.installRoot : paths.appPath;
  if (await removePathWithRetry(replacePath)) return;

  if (!quitPid) {
    if (managedExecutablePids.length > 0) {
      await forceQuitPidsAndConfirm(managedExecutablePids);
      if (await removePathWithRetry(replacePath, 6)) return;
    }
    throw new Error(`Failed to replace existing Rudder Desktop at ${replacePath}. Close Rudder and rerun start.`);
  }
  await forceQuitPidAndConfirm(quitPid);
  if (await removePathWithRetry(replacePath, 6)) return;

  throw new Error(`Failed to replace existing Rudder Desktop at ${replacePath}. Close Rudder and rerun start.`);
}

async function installPortableDesktop(
  installerPath: string,
  paths: DesktopInstallPaths,
  target: DesktopAssetTarget,
): Promise<void> {
  await mkdir(paths.installRoot, { recursive: true });

  if (target.platform === "linux") {
    await copyFile(installerPath, paths.appPath);
    await chmod(paths.appPath, 0o755);
    return;
  }

  const extractDir = await mkdtemp(path.join(tmpdir(), "rudder-desktop-extract."));
  try {
    await extractZip(installerPath, extractDir, target);
    if (target.platform === "macos") {
      const appSource = await findMacApp(extractDir);
      await copyPortableAppBundle(appSource, paths.appPath);
      return;
    }

    const appSource = await findWindowsAppDir(extractDir);
    await mkdir(path.dirname(paths.installRoot), { recursive: true });
    await copyPortableAppBundle(appSource, paths.installRoot);
  } finally {
    await rm(extractDir, { recursive: true, force: true });
  }
}

export async function copyPortableAppBundle(sourcePath: string, destinationPath: string): Promise<void> {
  if (process.platform === "win32") {
    await mkdir(destinationPath, { recursive: true });
    const command = buildWindowsRobocopyMirrorCommand(sourcePath, destinationPath);
    const result = spawnSync(command.command, command.args, {
      encoding: "utf8",
      stdio: ["ignore", "pipe", "pipe"],
    });
    if (isSuccessfulRobocopyExitCode(result.status)) return;
    throw new Error(formatCommandFailure(command.command, command.args, result.stdout, result.stderr));
  }

  await cp(sourcePath, destinationPath, { recursive: true, verbatimSymlinks: true });
}

async function removeMacQuarantine(paths: DesktopInstallPaths, target: DesktopAssetTarget): Promise<void> {
  if (target.platform !== "macos") return;
  const result = spawnSync("xattr", ["-dr", "com.apple.quarantine", paths.appPath], { stdio: "ignore" });
  if (result.status !== 0) {
    p.log.warn(`Could not remove macOS quarantine attributes from ${paths.appPath}.`);
  }
}

function quoteDesktopExec(value: string): string {
  return `"${value.replaceAll("\\", "\\\\").replaceAll("\"", "\\\"")}"`;
}

export function buildLinuxDesktopEntry(executablePath: string): string {
  return [
    "[Desktop Entry]",
    "Type=Application",
    "Name=Rudder",
    `Exec=${quoteDesktopExec(executablePath)}`,
    "Terminal=false",
    "Categories=Development;",
    "",
  ].join("\n");
}

async function writeLinuxLaunchers(paths: DesktopInstallPaths): Promise<void> {
  const desktopDir = path.join(homedir(), ".local", "share", "applications");
  await mkdir(desktopDir, { recursive: true });
  await writeFile(path.join(desktopDir, "rudder.desktop"), buildLinuxDesktopEntry(paths.executablePath), "utf8");

  const binDir = path.join(homedir(), ".local", "bin");
  await mkdir(binDir, { recursive: true });
  const wrapperPath = path.join(binDir, "rudder-desktop");
  const escaped = paths.executablePath.replaceAll("'", "'\"'\"'");
  await writeFile(wrapperPath, `#!/bin/sh\nexec '${escaped}' "$@"\n`, "utf8");
  await chmod(wrapperPath, 0o755);
}

function buildWindowsShortcutScript(executablePath: string): string {
  const appData = process.env.APPDATA?.trim() || path.join(homedir(), "AppData", "Roaming");
  const shortcutPath = path.join(appData, "Microsoft", "Windows", "Start Menu", "Programs", "Rudder.lnk");
  return [
    "$shell = New-Object -ComObject WScript.Shell",
    `$shortcut = $shell.CreateShortcut(${powershellQuote(shortcutPath)})`,
    `$shortcut.TargetPath = ${powershellQuote(executablePath)}`,
    `$shortcut.WorkingDirectory = ${powershellQuote(path.dirname(executablePath))}`,
    "$shortcut.Save()",
  ].join("; ");
}

async function createPlatformLaunchers(paths: DesktopInstallPaths, target: DesktopAssetTarget): Promise<void> {
  if (target.platform === "linux") {
    await writeLinuxLaunchers(paths);
    return;
  }
  if (target.platform === "windows") {
    const result = spawnSync("powershell.exe", [
      "-NoProfile",
      "-ExecutionPolicy",
      "Bypass",
      "-Command",
      buildWindowsShortcutScript(paths.executablePath),
    ], { stdio: "ignore" });
    if (result.status !== 0) p.log.warn("Could not create the Windows Start Menu shortcut.");
  }
}

function launchDesktop(paths: DesktopInstallPaths, target: DesktopAssetTarget): void {
  if (target.platform === "macos") {
    spawn("open", [paths.appPath], { detached: true, stdio: "ignore" }).unref();
    return;
  }
  if (target.platform === "windows") {
    spawn("cmd.exe", ["/c", "start", "", paths.executablePath], { detached: true, stdio: "ignore" }).unref();
    return;
  }
  spawn(paths.executablePath, [], { detached: true, stdio: "ignore" }).unref();
}

async function writeInstallMetadata(
  paths: DesktopInstallPaths,
  releaseTag: string,
  assetName: string,
  assetChecksum: string,
  assetKind: "full" | "shell" = "full",
): Promise<void> {
  mkdirSync(path.dirname(paths.metadataPath), { recursive: true });
  const metadata: DesktopInstallMetadata = {
    version: 1,
    releaseTag,
    assetName,
    assetChecksum,
    assetKind,
    installedAt: new Date().toISOString(),
  };
  mkdirSync(paths.installRoot, { recursive: true });
  await writeFile(paths.metadataPath, `${JSON.stringify(metadata, null, 2)}\n`, "utf8");
}

async function runStartPhase<T>(
  message: string,
  successMessage: string,
  task: () => Promise<T> | T,
  progressPhase?: DesktopUpdateProgressPhase | null,
): Promise<T> {
  if (progressPhase) {
    writeDesktopProgress({ phase: progressPhase, message });
  }
  const spinner = p.spinner();
  spinner.start(message);
  try {
    const result = await task();
    spinner.stop(successMessage);
    if (progressPhase) {
      writeDesktopProgress({ phase: progressPhase, message: successMessage });
    }
    return result;
  } catch (error) {
    spinner.stop(pc.red(`${message} failed.`));
    if (progressPhase) {
      writeDesktopProgress({
        phase: "failed",
        message: `${message} failed.`,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    throw error;
  }
}

export async function startCommand(opts: StartCommandOptions): Promise<void> {
  applyLocalEnvProfile(opts);
  applyDataDirOverride(opts as DataDirOptionLike);
  const serverOnly = opts.serverOnly === true;
  const installApp = !serverOnly && opts.desktop !== false;
  const requestedDesktopMode = parseDesktopLaunchMode(opts.desktopMode);
  const desktopTarget = installApp ? resolveDesktopAssetTarget() : null;
  // Commander supplies the explicit default `auto`. Direct programmatic calls
  // that predate desktop-mode keep their native behavior unless they opt in.
  const automaticCompatibilityCheck = requestedDesktopMode === "auto" && opts.desktopMode !== undefined;
  const smartAppControlState = desktopTarget?.platform === "windows" && automaticCompatibilityCheck
    ? detectSmartAppControlState()
    : "unknown";
  const desktopMode = installApp
    ? resolveDesktopLaunchMode({
        requested: requestedDesktopMode,
        platform: process.platform,
        smartAppControlState,
      })
    : "native";
  if (desktopMode === "browser" && desktopTarget?.platform !== "windows") {
    throw new Error("Rudder browser-app compatibility mode is currently available only on Windows.");
  }
  const installDesktop = installApp && desktopMode === "native";
  const installBrowserApp = installApp && desktopMode === "browser";
  const browserLocalProfile = installBrowserApp
    ? resolveActiveLocalEnvProfile() ?? applyLocalEnvProfile({ localEnv: "prod_local" })
    : null;
  // Browser-app mode depends on the persistent CLI and matching server runtime;
  // internal Desktop update flags must not leave the compatibility handoff on
  // the soon-to-be-obsolete packaged CLI/runtime.
  const installCli = opts.cli !== false || installBrowserApp;
  const installRuntime = opts.runtime !== false || installBrowserApp;
  const repo = opts.repo?.trim() || DEFAULT_DESKTOP_RELEASE_REPO;
  const version = opts.targetVersion?.trim() || opts.version?.trim() || resolveCurrentCliVersion();
  const dryRun = opts.dryRun === true;
  const desktopProgressJson = opts.desktopProgressJson === true;
  const desktopRuntimeBestEffort = opts.desktopRuntimeBestEffort === true && installDesktop;
  const exactDesktopAssetPath = opts.desktopAssetPath?.trim() || null;
  const exactDesktopAssetChecksum = opts.desktopAssetChecksum?.trim() || null;
  const exactDesktopAssetName = opts.desktopAssetName?.trim() || null;
  const exactDesktopReleaseDigest = opts.desktopReleaseDigest?.trim() || null;
  if (exactDesktopAssetPath || exactDesktopAssetChecksum || exactDesktopAssetName || exactDesktopReleaseDigest) {
    if (!exactDesktopAssetPath || !exactDesktopAssetChecksum || !exactDesktopAssetName || !exactDesktopReleaseDigest) {
      throw new Error("Exact Desktop asset mode requires path, checksum, asset name, and release digest.");
    }
    if (!path.isAbsolute(exactDesktopAssetPath) || exactDesktopAssetName.includes("/") || !/^[a-f0-9]{64}$/iu.test(exactDesktopAssetChecksum) || !/^[a-f0-9]{64}$/iu.test(exactDesktopReleaseDigest)) {
      throw new Error("Exact Desktop asset mode received invalid candidate identity.");
    }
    if (opts.desktopAssetKind && opts.desktopAssetKind !== "full" && opts.desktopAssetKind !== "shell") {
      throw new Error("Exact Desktop asset mode received an invalid asset kind.");
    }
  }
  let runtimeSupportsShellAssets = false;

  if (desktopProgressJson) {
    process.stdout.on("error", (error: NodeJS.ErrnoException) => {
      if (error.code !== "EPIPE") throw error;
    });
  }

  if (!installCli && !installApp && !installRuntime) {
    throw new Error("Nothing to start. Remove --no-cli, --no-runtime, --no-desktop, or --server-only.");
  }

  p.intro(pc.bgCyan(pc.black(serverOnly ? " rudder start --server-only " : " rudder start ")));

  if (installBrowserApp) {
    p.log.warn(
      requestedDesktopMode === "browser"
        ? "Using the requested Windows browser-app compatibility mode."
        : "Windows Smart App Control is on, so Rudder will use browser-app compatibility mode instead of the unsigned Desktop executable.",
    );
    p.log.message(
      pc.dim(
        "This is a loopback-only local_trusted client without the packaged Desktop Account Gate. Local data stays in place; Electron-only Browser and App Builder bridges are unavailable.",
      ),
    );
  }

  if (opts.versionCheck !== false) {
    const updateNotice = await getCliUpdateNotice(version);
    if (updateNotice) p.log.warn(updateNotice);
  }

  if (installRuntime) {
    p.log.step("Preparing Rudder runtime");
    if (desktopProgressJson && desktopRuntimeBestEffort) {
      writeDesktopProgress({
        phase: "preparing_runtime",
        message: "Preparing the lightweight Desktop update runtime; the full package will be used if this takes too long.",
      });
      await waitForDesktopRuntimeSmokeEvidence("RUDDER_DESKTOP_SMOKE_RUNTIME_PREPARING_DELAY_MS");
    }
    if (dryRun) {
      p.log.message(`[dry-run] Would install or reuse ${pc.cyan(`@rudderhq/server@${version}`)} in the Rudder runtime cache.`);
    } else {
      const spinner = p.spinner();
      spinner.start("Installing or reusing Rudder runtime...");
      try {
        const runtime = await ensureRuntimeInstalled({
          version,
          preparePostgresPayload: true,
          ...(desktopRuntimeBestEffort
            ? {
                timeoutMs: DESKTOP_RUNTIME_PREPARE_TIMEOUT_MS,
                cleanupIncompleteOnFailure: true,
                pruneRuntimeCache: false,
                allowLatestFallback: false,
              }
            : {}),
        });
        runtimeSupportsShellAssets = runtimeSupportsDesktopShellAssets(version, runtime);
        spinner.stop(
          runtime.status === "hit"
            ? `Rudder runtime cache hit at ${pc.cyan(runtime.cacheDir)}.`
            : `Rudder runtime installed at ${pc.cyan(runtime.cacheDir)}.`,
        );
        if (!runtime.postgresPayloadBinDir && installDesktop) {
          p.log.warn("Rudder runtime cache has no PostgreSQL 18.4 payload; the full portable Desktop asset will be used.");
        }
        if (!runtimeSupportsShellAssets && installDesktop) {
          p.log.warn("Rudder runtime did not resolve to the exact Desktop version; the full portable Desktop asset will be used.");
        }
        if (desktopProgressJson && desktopRuntimeBestEffort) {
          writeDesktopProgress({
            phase: "preparing_runtime",
            message: runtimeSupportsShellAssets
              ? "Lightweight Desktop update runtime is ready."
              : "Lightweight runtime is unavailable; continuing with the full Desktop package.",
          });
        }
      } catch (error) {
        spinner.stop(pc.red("Rudder runtime installation failed."));
        if (error instanceof RuntimeInstallError && error.output) {
          p.log.message(pc.dim(error.output));
        }
        if (!desktopRuntimeBestEffort) throw error;
        const detail = error instanceof Error ? error.message : String(error);
        p.log.warn(`Lightweight Desktop update runtime preparation failed; continuing with the full portable asset. ${detail}`);
        if (desktopProgressJson) {
          writeDesktopProgress({
            phase: "preparing_runtime",
            message: "Lightweight runtime preparation did not finish; continuing with the full Desktop package.",
          });
          await waitForDesktopRuntimeSmokeEvidence("RUDDER_DESKTOP_SMOKE_RUNTIME_FALLBACK_DELAY_MS");
        }
      }
    }
  }

  if (installCli) {
    const installSpec = resolveCliInstallSpec(version);
    const command = `npm install --global ${installSpec}`;
    const installedVersion = getGlobalInstalledPackageVersion(CLI_NPM_PACKAGE_NAME);
    p.log.step("Preparing persistent CLI");
    if (isPersistentCliVersionCurrent(version, installedVersion)) {
      p.log.success(`${pc.cyan("rudder")} CLI ${version} is already installed.`);
    } else if (dryRun) {
      p.log.message(`[dry-run] ${command}`);
    } else {
      p.log.message(pc.dim(`Running: ${command}`));
      const spinner = p.spinner();
      spinner.start("Installing persistent CLI...");
      let result: ReturnType<typeof installPersistentCli>;
      try {
        result = installPersistentCli({ installSpec });
      } catch (error) {
        spinner.stop(pc.red("Persistent CLI installation failed."));
        throw error;
      }
      if (!result.ok) {
        spinner.stop(pc.red("Persistent CLI installation failed."));
        if (result.output) p.log.message(pc.dim(result.output));
        throw new Error(`Persistent CLI installation failed. Re-run manually: ${result.command}`);
      }
      spinner.stop(`${pc.cyan("rudder")} CLI installed.`);
    }
  }

  if (installBrowserApp) {
    const target = desktopTarget!;
    const installRoot = opts.desktopInstallDir
      ? path.resolve(opts.desktopInstallDir)
      : resolveDefaultDesktopInstallRoot(target);
    const installPaths = resolveDesktopInstallPaths(target, installRoot);
    const runtimeVersion = version;
    if (!browserLocalProfile) throw new Error("Rudder browser-app requires a local environment profile.");
    const dataDir = resolveRudderHomeDir();
    p.log.step("Preparing Windows browser app");
    p.log.message(`Runtime: ${pc.cyan(runtimeVersion)}`);
    p.log.message(`Workspace: ${pc.cyan(`${browserLocalProfile.name}/${browserLocalProfile.instanceId}`)}`);

    if (dryRun) {
      p.log.message(
        `[dry-run] Would create a Rudder Start Menu shortcut backed by Node and open the local workspace in Microsoft Edge app mode.`,
      );
      p.outro(pc.green("Dry run complete."));
      return;
    }

    const cliEntryPath = resolveGlobalInstalledCliEntry();
    if (!(await pathExists(cliEntryPath))) {
      throw new Error(`Persistent Rudder CLI entry was not found at ${cliEntryPath}.`);
    }
    await mkdir(installRoot, { recursive: true });
    const edgePath = resolveEdgeExecutable();
    const nativeIconPath = await pathExists(installPaths.executablePath)
      ? installPaths.executablePath
      : null;
    const shortcutPath = createWindowsBrowserAppShortcut({
      nodePath: process.execPath,
      cliEntryPath,
      localEnv: browserLocalProfile.name,
      dataDir,
      runtimeVersion,
      workingDirectory: installRoot,
      iconPath: nativeIconPath ?? edgePath,
    });
    p.log.success(`Rudder browser-app shortcut is ready at ${pc.cyan(shortcutPath)}.`);

    let applySignalController: ReturnType<typeof createDesktopApplySignalController> | null = null;
    if (desktopProgressJson && opts.desktopWaitForApply === true) {
      writeDesktopProgress({
        phase: "ready_to_install",
        message: "Windows browser-app compatibility mode is ready.",
        percent: 100,
      });
      applySignalController = createDesktopApplySignalController();
      await applySignalController.waitForInitialSignal();
      applySignalController.close();
      writeDesktopProgress({
        phase: "preparing_restart",
        message: "Starting the Windows browser app...",
        percent: 100,
      });
    }

    if (opts.open !== false) {
      const launch = await launchDetachedBrowserApp({
        cliEntryPath,
        localEnv: browserLocalProfile.name,
        dataDir,
        runtimeVersion,
        open: true,
      });
      p.log.success(`Rudder browser app opened at ${pc.cyan(launch.boardUrl)}.`);
      p.log.message(pc.dim(`Background runtime log: ${launch.logPath}`));
      if (desktopProgressJson) {
        writeDesktopProgress({
          phase: "closing",
          message: "Rudder browser app is ready. You can close the native Desktop window.",
          percent: 100,
        });
      }
    }
    p.outro(pc.green("Rudder start complete."));
    return;
  }

  if (installDesktop) {
    const downloadSource = resolveDesktopDownloadSource(opts.downloadSource);
    const mirrorBaseUrl = resolveDesktopReleaseMirrorBaseUrl(repo);
    const smokeReleaseBaseUrls = resolveDesktopSmokeReleaseBaseUrls();
    const target = desktopTarget!;
    const tag = resolveDesktopReleaseTag(version);
    const installRoot = opts.desktopInstallDir
      ? path.resolve(opts.desktopInstallDir)
      : resolveDefaultDesktopInstallRoot(target);
    const installPaths = resolveDesktopInstallPaths(target, installRoot);
    const outputDir = opts.outputDir
      ? path.resolve(opts.outputDir)
      : await mkdtemp(path.join(tmpdir(), "rudder-desktop-installer."));

    p.log.step("Installing desktop app");
    p.log.message(`Release: ${pc.cyan(`${repo}@${tag}`)}`);
    p.log.message(`Target: ${pc.cyan(`${target.platform}/${target.arch}`)}`);
    p.log.message(`Install: ${pc.cyan(installPaths.appPath)}`);
    p.log.message(`Download source: ${pc.cyan(downloadSource)}`);

    if (dryRun) {
      p.log.message(`[dry-run] Would resolve, download, verify, install, and ${opts.open === false ? "not launch" : "launch"} Rudder Desktop.`);
      p.outro(pc.green("Dry run complete."));
      return;
    }

    await withDesktopInstallLock(installPaths, async () => {
      const progressFactory: ProgressReporterFactory = desktopProgressJson
        ? createDesktopProgressFactory()
        : createByteProgress;
      let releaseTag: string;
      let selectedAsset: GithubReleaseAsset;
      let selectedAssetKind: "full" | "shell";
      let expectedChecksum: string;
      let cachedAsset: Awaited<ReturnType<typeof downloadDesktopAssetWithCache>> | null = null;
      let assetCandidates: DesktopAssetCandidate[] = [];
      let checksums = new Map<string, string>();
      let downloadOrigins: DesktopDownloadOrigin[] = ["github"];

      if (exactDesktopAssetPath) {
        releaseTag = tag;
        selectedAsset = {
          name: exactDesktopAssetName!,
          browser_download_url: "",
        };
        selectedAssetKind = opts.desktopAssetKind ?? "full";
        expectedChecksum = normalizeDesktopAssetChecksum(exactDesktopAssetChecksum!);
        const computedReleaseDigest = createHash("sha256")
          .update(JSON.stringify({
            releaseTag,
            assetName: selectedAsset.name,
            assetChecksum: expectedChecksum,
            assetKind: selectedAssetKind,
            platform: target.platform,
            arch: target.arch,
          }))
          .digest("hex");
        if (computedReleaseDigest !== exactDesktopReleaseDigest!.toLowerCase()) {
          throw new Error("Exact Desktop asset release digest does not match the candidate identity.");
        }
        const descriptor = await stat(exactDesktopAssetPath);
        if (!descriptor.isFile()) throw new Error("Exact Desktop asset must be a regular file.");
        const linkDescriptor = await lstat(exactDesktopAssetPath);
        if (linkDescriptor.isSymbolicLink()) throw new Error("Exact Desktop asset must not be a symbolic link.");
        const checksum = await runStartPhase(
          "Verifying staged Desktop checksum...",
          `Verified ${pc.cyan(path.basename(exactDesktopAssetPath))}.`,
          () => assertChecksumMatch(exactDesktopAssetPath, expectedChecksum),
          desktopProgressJson ? "verifying_checksum" : null,
        );
        cachedAsset = { path: exactDesktopAssetPath, checksum, cacheStatus: "hit" };
      } else {
        const directReleaseVersion = resolveDesktopReleaseVersion(tag);
        let release: GithubRelease | null = null;
        try {
          release = await runStartPhase(
            "Resolving Desktop release...",
            "Desktop release resolved.",
            () => fetchGithubRelease(repo, tag, smokeReleaseBaseUrls.apiBaseUrl),
            desktopProgressJson ? "resolving_release" : null,
          );
        } catch (error) {
          if (!directReleaseVersion) throw error;
          p.log.warn(
            `Desktop release metadata could not be resolved; falling back to deterministic download URLs. ${formatFetchError(error)}`,
          );
        }

        releaseTag = release?.tag_name ?? (directReleaseVersion ? tag : "");
        if (!releaseTag) throw new Error(`Unable to resolve Rudder Desktop release tag for ${repo}@${tag}.`);
        assetCandidates = resolveDesktopAssetCandidates({
          releaseAssets: release?.assets ?? [],
          target,
          repo,
          tag,
          directReleaseVersion,
          allowShellAssets: runtimeSupportsShellAssets,
          downloadBaseUrl: smokeReleaseBaseUrls.downloadBaseUrl,
        });
        if (assetCandidates.length === 0) {
          throw new Error(`No Rudder Desktop portable asset found for ${target.platform}/${target.arch} in ${repo}@${releaseTag}.`);
        }
        const checksumAsset = selectChecksumAsset(release?.assets ?? [])
          ?? (directReleaseVersion
            ? buildGithubReleaseAsset(
              repo,
              tag,
              DESKTOP_CHECKSUM_ASSET_NAME,
              smokeReleaseBaseUrls.downloadBaseUrl,
            )
            : null);
        if (!checksumAsset) {
          throw new Error("Desktop release is missing SHASUMS256.txt.");
        }
        downloadOrigins = await resolveDesktopDownloadOrigins({
          source: downloadSource,
          checksumAsset,
          tag: releaseTag,
          mirrorBaseUrl,
        });
        if (downloadOrigins[0] === "mirror") {
          p.log.message("Using the China release mirror with GitHub fallback.");
        } else if (downloadSource !== "global" && mirrorBaseUrl) {
          p.log.message("Using GitHub for this network path.");
        }
        // GitHub's checksum manifest remains the trust root. The mirror copy is
        // probed for availability only and never defines accepted asset bytes.
        checksums = await downloadChecksums(checksumAsset, outputDir, progressFactory);
        let selectedCandidate: ChecksummedDesktopAssetCandidate;
        try {
          selectedCandidate = selectChecksummedDesktopAssetCandidate(assetCandidates, checksums);
        } catch {
          throw new Error(`No checksummed Rudder Desktop asset found for ${target.platform}/${target.arch} in ${repo}@${releaseTag}.`);
        }
        for (const warning of selectedCandidate.warnings) p.log.warn(warning);
        selectedAsset = withDesktopDownloadOrigins(selectedCandidate.asset, downloadOrigins, {
          mirrorBaseUrl,
          tag: releaseTag,
        });
        selectedAssetKind = selectedCandidate.kind;
        expectedChecksum = selectedCandidate.expectedChecksum;
      }

      const metadata = await readInstallMetadata(installPaths.metadataPath);
      if (
        isInstalledDesktopCurrent(metadata, releaseTag, selectedAsset.name, expectedChecksum) &&
        await pathExists(installPaths.executablePath)
      ) {
        p.log.success(`Rudder Desktop is already installed at ${pc.cyan(installPaths.appPath)}.`);
        await runStartPhase(
          "Refreshing Desktop launchers...",
          "Desktop launchers ready.",
          async () => {
            await removeMacQuarantine(installPaths, target);
            await createPlatformLaunchers(installPaths, target);
          },
          desktopProgressJson ? "preparing_restart" : null,
        );
      } else {
        if (!exactDesktopAssetPath) try {
          cachedAsset = await downloadDesktopAssetWithCache(selectedAsset, expectedChecksum, {
            outputDir,
            progressFactory,
          });
        } catch (error) {
          const fullCandidate = assetCandidates.find((candidate) => candidate.kind === "full");
          if (selectedAssetKind !== "shell" || !fullCandidate) throw error;
          p.log.warn(
            `Layered Desktop shell asset download failed; falling back to the full portable asset. ${formatFetchError(error)}`,
          );
          selectedAsset = withDesktopDownloadOrigins(fullCandidate.asset, downloadOrigins, {
            mirrorBaseUrl,
            tag: releaseTag,
          });
          selectedAssetKind = fullCandidate.kind;
          expectedChecksum = resolveAssetChecksum(checksums, selectedAsset.name);
          cachedAsset = await downloadDesktopAssetWithCache(selectedAsset, expectedChecksum, {
            outputDir,
            progressFactory,
          });
        }
        if (!cachedAsset) throw new Error("Desktop update did not produce a verified asset.");
        const verifiedAsset = cachedAsset;
        if (verifiedAsset.cacheStatus === "hit") {
          p.log.success(`Desktop asset cache hit at ${pc.cyan(verifiedAsset.path)}.`);
          if (desktopProgressJson) {
            writeDesktopProgress({
              phase: "downloading_asset",
              message: `Desktop asset cache hit for ${selectedAsset.name}.`,
              percent: 100,
            });
          }
        }
        const checksum = await runStartPhase(
          "Verifying Desktop checksum...",
          `Verified ${pc.cyan(path.basename(verifiedAsset.path))}.`,
          () => assertChecksumMatch(verifiedAsset.path, expectedChecksum),
          desktopProgressJson ? "verifying_checksum" : null,
        );

        if (opts.desktopPrepareOnly === true) {
          writeDesktopProgress({
            phase: "prepared",
            message: "Desktop update is downloaded and verified.",
            percent: 100,
            assetName: selectedAsset.name,
            assetChecksum: checksum,
            assetKind: selectedAssetKind,
            stagedArtifactPath: path.resolve(verifiedAsset.path),
            stagedArtifactDigest: checksum,
            releaseDigest: createHash("sha256")
              .update(JSON.stringify({
                releaseTag,
                assetName: selectedAsset.name,
                assetChecksum: checksum,
                assetKind: selectedAssetKind,
                platform: target.platform,
                arch: target.arch,
              }))
              .digest("hex"),
          });
          return;
        }

        let applySignal: { force: boolean } | null = null;
        let applySignalController: ReturnType<typeof createDesktopApplySignalController> | null = null;
        if (desktopProgressJson && opts.desktopWaitForApply === true) {
          applySignalController = createDesktopApplySignalController();
          writeDesktopProgress({
            phase: "ready_to_install",
            message: "Desktop update is downloaded and verified.",
            percent: 100,
          });
          applySignal = await applySignalController.waitForInitialSignal();
          writeDesktopProgress({
            phase: "preparing_restart",
            message: applySignal.force
              ? "Applying Desktop update and quitting active runs..."
              : "Applying Desktop update...",
          });
        }

        try {
          await runStartPhase(
            "Replacing existing Rudder Desktop if needed...",
            "Existing Desktop install is ready for replacement.",
            () => prepareForDesktopReplace(installPaths, target, {
              waitForActiveRuns: opts.waitForActiveRuns === true,
              forceUpdate: applySignal?.force === true,
              waitForForceUpdate: applySignalController?.waitForForceRequest,
              onActiveRunsWaiting: desktopProgressJson
                ? (totalRuns) => writeDesktopProgress({
                  phase: "waiting_for_active_runs",
                  message: `Waiting for ${totalRuns} running agent run${totalRuns === 1 ? "" : "s"} before replacing Desktop.`,
                  totalRuns,
                })
                : undefined,
            }),
            desktopProgressJson ? (opts.waitForActiveRuns === true ? "waiting_for_active_runs" : "preparing_restart") : null,
          );
        } finally {
          applySignalController?.close();
        }
        await runStartPhase(
          "Installing portable Desktop app...",
          `Installed Rudder Desktop to ${pc.cyan(installPaths.appPath)}.`,
            () => installPortableDesktop(verifiedAsset.path, installPaths, target),
          desktopProgressJson ? "preparing_restart" : null,
        );
        await runStartPhase(
          "Preparing Desktop launchers...",
          "Desktop launchers ready.",
          async () => {
            await removeMacQuarantine(installPaths, target);
            await createPlatformLaunchers(installPaths, target);
          },
          desktopProgressJson ? "preparing_restart" : null,
        );
        await writeInstallMetadata(installPaths, releaseTag, selectedAsset.name, checksum, selectedAssetKind);
      }

      const desktopAssetPrune = await maybePruneDesktopAssetCache({
        protectedChecksums: [expectedChecksum],
      });
      if (desktopAssetPrune) {
        if (desktopAssetPrune.deleted.length > 0) {
          p.log.success(
            `Pruned ${desktopAssetPrune.deleted.length} old Desktop asset cache(s), freed ${formatBytes(desktopAssetPrune.freedBytes)}.`,
          );
        }
        for (const warning of desktopAssetPrune.warnings) p.log.warn(warning);
      }

      if (opts.open !== false) {
        await runStartPhase(
          "Launching Rudder Desktop...",
          "Rudder Desktop launched.",
          () => launchDesktop(installPaths, target),
          desktopProgressJson ? "closing" : null,
        );
      }
    });
  } else if (serverOnly) {
    p.log.step("Server-only install");
    p.log.message("Desktop app installation was skipped. Start the server with `rudder run` and open the printed local URL in a browser.");
  }

  p.outro(pc.green("Rudder start complete."));
}
